import React, { useState } from 'react';
import { Container, Paper, Typography, Grid, TextField, Button, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch, Box, Divider } from '@material-ui/core';
import { ApolloProvider } from '@apollo/client';
import { Query, Mutation } from '@apollo/client/react/components';
import { SnackbarProvider, useSnackbar } from 'notistack';
import gql from 'graphql-tag';
import client from '../../lib/apolloClient';
import SuccessionThemeProvider from '../../components/Succession/SuccessionThemeProvider';
import { DEFAULT_REVENUE_LINE_TERMINOLOGY, getRevenueLineTerminology } from '../../components/Succession/revenueLineTerminology';

const ADMIN_QUERY = gql`
  query MultiLineAdmin {
    company {
      id
      rj_use_owners_comp_direct_expenses
      multi_line_mor_weight
      multi_line_dcf_weight
      multi_line_ebitda_weight
      multi_line_asset_based_weight
      multi_line_dcf_projection_years

      confidence_revenue_weight
      confidence_expenses_weight
      confidence_normalization_weight

      wm_cost_of_equity_low wm_cost_of_equity_mid wm_cost_of_equity_high
      wm_ebitda_multiple_low wm_ebitda_multiple_mid wm_ebitda_multiple_high
      wm_mor_recurring_low wm_mor_recurring_mid wm_mor_recurring_high
      wm_mor_non_recurring_low wm_mor_non_recurring_mid wm_mor_non_recurring_high

      li_cost_of_equity_low li_cost_of_equity_mid li_cost_of_equity_high
      li_ebitda_multiple_low li_ebitda_multiple_mid li_ebitda_multiple_high
      li_mor_first_year_low li_mor_first_year_mid li_mor_first_year_high
      li_mor_renewal_low li_mor_renewal_mid li_mor_renewal_high
      li_mor_override_low li_mor_override_mid li_mor_override_high

      pc_cost_of_equity_low pc_cost_of_equity_mid pc_cost_of_equity_high
      pc_ebitda_multiple_low pc_ebitda_multiple_mid pc_ebitda_multiple_high
      pc_mor_personal_lines_low pc_mor_personal_lines_mid pc_mor_personal_lines_high
      pc_mor_commercial_lines_low pc_mor_commercial_lines_mid pc_mor_commercial_lines_high

      group_cost_of_equity_low group_cost_of_equity_mid group_cost_of_equity_high
      group_ebitda_multiple_low group_ebitda_multiple_mid group_ebitda_multiple_high
      group_mor_first_year_low group_mor_first_year_mid group_mor_first_year_high
      group_mor_renewal_low group_mor_renewal_mid group_mor_renewal_high
      group_mor_override_low group_mor_override_mid group_mor_override_high
      revenue_line_terminology
    }
    valuationFactors { id name category value appliesToLine inventoryAttributeMapping enabled }
    intangibleAssets { id name appliesToLine valuationMethod defaultValue enabled }
    multiLineExpenseCategories { id companyId key label description groupName sortOrder enabled }
    industryBenchmarks { id tierName year minRevenue maxRevenue ownerSalaryDrawPercent totalOperatingExpensesPercent }
  }
`;

const IMPORT_BENCHMARKS = gql`
  mutation ImportIndustryBenchmarks($csvContent: String!, $year: Int) {
    importIndustryBenchmarks(csvContent: $csvContent, year: $year) {
      success
      createdCount
      updatedCount
      errors
      benchmarks { id tierName year minRevenue maxRevenue ownerSalaryDrawPercent totalOperatingExpensesPercent }
    }
  }
`;

const UPDATE_SETTINGS = gql`
  mutation UpdateSettings(
    $multiLineMorWeight: Float,
    $multiLineDcfWeight: Float,
    $multiLineEbitdaWeight: Float,
    $multiLineAssetBasedWeight: Float,
    $multiLineDcfProjectionYears: Int,
    $confidenceRevenueWeight: Float,
    $confidenceExpensesWeight: Float,
    $confidenceNormalizationWeight: Float,
    $wmCostOfEquityLow: Float, $wmCostOfEquityMid: Float, $wmCostOfEquityHigh: Float,
    $wmEbitdaMultipleLow: Float, $wmEbitdaMultipleMid: Float, $wmEbitdaMultipleHigh: Float,
    $wmMorRecurringLow: Float, $wmMorRecurringMid: Float, $wmMorRecurringHigh: Float,
    $wmMorNonRecurringLow: Float, $wmMorNonRecurringMid: Float, $wmMorNonRecurringHigh: Float,
    $liCostOfEquityLow: Float, $liCostOfEquityMid: Float, $liCostOfEquityHigh: Float,
    $liEbitdaMultipleLow: Float, $liEbitdaMultipleMid: Float, $liEbitdaMultipleHigh: Float,
    $liMorFirstYearLow: Float, $liMorFirstYearMid: Float, $liMorFirstYearHigh: Float,
    $liMorRenewalLow: Float, $liMorRenewalMid: Float, $liMorRenewalHigh: Float,
    $liMorOverrideLow: Float, $liMorOverrideMid: Float, $liMorOverrideHigh: Float,
    $pcCostOfEquityLow: Float, $pcCostOfEquityMid: Float, $pcCostOfEquityHigh: Float,
    $pcEbitdaMultipleLow: Float, $pcEbitdaMultipleMid: Float, $pcEbitdaMultipleHigh: Float,
    $pcMorPersonalLinesLow: Float, $pcMorPersonalLinesMid: Float, $pcMorPersonalLinesHigh: Float,
    $pcMorCommercialLinesLow: Float, $pcMorCommercialLinesMid: Float, $pcMorCommercialLinesHigh: Float,
    $groupCostOfEquityLow: Float, $groupCostOfEquityMid: Float, $groupCostOfEquityHigh: Float,
    $groupEbitdaMultipleLow: Float, $groupEbitdaMultipleMid: Float, $groupEbitdaMultipleHigh: Float,
    $groupMorFirstYearLow: Float, $groupMorFirstYearMid: Float, $groupMorFirstYearHigh: Float,
    $groupMorRenewalLow: Float, $groupMorRenewalMid: Float, $groupMorRenewalHigh: Float,
    $groupMorOverrideLow: Float, $groupMorOverrideMid: Float, $groupMorOverrideHigh: Float,
    $rjUseOwnersCompDirectExpenses: Boolean,
    $revenueLineTerminology: JSON
  ) {
    updateMultiLineSettings(
      multiLineMorWeight: $multiLineMorWeight,
      multiLineDcfWeight: $multiLineDcfWeight,
      multiLineEbitdaWeight: $multiLineEbitdaWeight,
      multiLineAssetBasedWeight: $multiLineAssetBasedWeight,
      multiLineDcfProjectionYears: $multiLineDcfProjectionYears,
      confidenceRevenueWeight: $confidenceRevenueWeight,
      confidenceExpensesWeight: $confidenceExpensesWeight,
      confidenceNormalizationWeight: $confidenceNormalizationWeight,

      wmCostOfEquityLow: $wmCostOfEquityLow, wmCostOfEquityMid: $wmCostOfEquityMid, wmCostOfEquityHigh: $wmCostOfEquityHigh,
      wmEbitdaMultipleLow: $wmEbitdaMultipleLow, wmEbitdaMultipleMid: $wmEbitdaMultipleMid, wmEbitdaMultipleHigh: $wmEbitdaMultipleHigh,
      wmMorRecurringLow: $wmMorRecurringLow, wmMorRecurringMid: $wmMorRecurringMid, wmMorRecurringHigh: $wmMorRecurringHigh,
      wmMorNonRecurringLow: $wmMorNonRecurringLow, wmMorNonRecurringMid: $wmMorNonRecurringMid, wmMorNonRecurringHigh: $wmMorNonRecurringHigh,

      liCostOfEquityLow: $liCostOfEquityLow, liCostOfEquityMid: $liCostOfEquityMid, liCostOfEquityHigh: $liCostOfEquityHigh,
      liEbitdaMultipleLow: $liEbitdaMultipleLow, liEbitdaMultipleMid: $liEbitdaMultipleMid, liEbitdaMultipleHigh: $liEbitdaMultipleHigh,
      liMorFirstYearLow: $liMorFirstYearLow, liMorFirstYearMid: $liMorFirstYearMid, liMorFirstYearHigh: $liMorFirstYearHigh,
      liMorRenewalLow: $liMorRenewalLow, liMorRenewalMid: $liMorRenewalMid, liMorRenewalHigh: $liMorRenewalHigh,
      liMorOverrideLow: $liMorOverrideLow, liMorOverrideMid: $liMorOverrideMid, liMorOverrideHigh: $liMorOverrideHigh,

      pcCostOfEquityLow: $pcCostOfEquityLow, pcCostOfEquityMid: $pcCostOfEquityMid, pcCostOfEquityHigh: $pcCostOfEquityHigh,
      pcEbitdaMultipleLow: $pcEbitdaMultipleLow, pcEbitdaMultipleMid: $pcEbitdaMultipleMid, pcEbitdaMultipleHigh: $pcEbitdaMultipleHigh,
      pcMorPersonalLinesLow: $pcMorPersonalLinesLow, pcMorPersonalLinesMid: $pcMorPersonalLinesMid, pcMorPersonalLinesHigh: $pcMorPersonalLinesHigh,
      pcMorCommercialLinesLow: $pcMorCommercialLinesLow, pcMorCommercialLinesMid: $pcMorCommercialLinesMid, pcMorCommercialLinesHigh: $pcMorCommercialLinesHigh,

      groupCostOfEquityLow: $groupCostOfEquityLow, groupCostOfEquityMid: $groupCostOfEquityMid, groupCostOfEquityHigh: $groupCostOfEquityHigh,
      groupEbitdaMultipleLow: $groupEbitdaMultipleLow, groupEbitdaMultipleMid: $groupEbitdaMultipleMid, groupEbitdaMultipleHigh: $groupEbitdaMultipleHigh,
      groupMorFirstYearLow: $groupMorFirstYearLow, groupMorFirstYearMid: $groupMorFirstYearMid, groupMorFirstYearHigh: $groupMorFirstYearHigh,
      groupMorRenewalLow: $groupMorRenewalLow, groupMorRenewalMid: $groupMorRenewalMid, groupMorRenewalHigh: $groupMorRenewalHigh,
      groupMorOverrideLow: $groupMorOverrideLow, groupMorOverrideMid: $groupMorOverrideMid, groupMorOverrideHigh: $groupMorOverrideHigh,
      rjUseOwnersCompDirectExpenses: $rjUseOwnersCompDirectExpenses,
      revenueLineTerminology: $revenueLineTerminology
    ) {
      id
      rj_use_owners_comp_direct_expenses
      multi_line_mor_weight
      multi_line_dcf_weight
      multi_line_ebitda_weight
      multi_line_asset_based_weight
      multi_line_dcf_projection_years
      confidence_revenue_weight
      confidence_expenses_weight
      confidence_normalization_weight

      wm_cost_of_equity_low wm_cost_of_equity_mid wm_cost_of_equity_high
      wm_ebitda_multiple_low wm_ebitda_multiple_mid wm_ebitda_multiple_high
      wm_mor_recurring_low wm_mor_recurring_mid wm_mor_recurring_high
      wm_mor_non_recurring_low wm_mor_non_recurring_mid wm_mor_non_recurring_high

      li_cost_of_equity_low li_cost_of_equity_mid li_cost_of_equity_high
      li_ebitda_multiple_low li_ebitda_multiple_mid li_ebitda_multiple_high
      li_mor_first_year_low li_mor_first_year_mid li_mor_first_year_high
      li_mor_renewal_low li_mor_renewal_mid li_mor_renewal_high
      li_mor_override_low li_mor_override_mid li_mor_override_high

      pc_cost_of_equity_low pc_cost_of_equity_mid pc_cost_of_equity_high
      pc_ebitda_multiple_low pc_ebitda_multiple_mid pc_ebitda_multiple_high
      pc_mor_personal_lines_low pc_mor_personal_lines_mid pc_mor_personal_lines_high
      pc_mor_commercial_lines_low pc_mor_commercial_lines_mid pc_mor_commercial_lines_high

      group_cost_of_equity_low group_cost_of_equity_mid group_cost_of_equity_high
      group_ebitda_multiple_low group_ebitda_multiple_mid group_ebitda_multiple_high
      group_mor_first_year_low group_mor_first_year_mid group_mor_first_year_high
      group_mor_renewal_low group_mor_renewal_mid group_mor_renewal_high
      group_mor_override_low group_mor_override_mid group_mor_override_high
      revenue_line_terminology
    }
  }
`;

const CREATE_FACTOR = gql`
  mutation CreateFactor($name: String!, $category: String!, $value: Float!, $appliesToLine: String, $inventoryAttributeMapping: String, $enabled: Boolean) {
    createValuationFactor(name: $name, category: $category, value: $value, appliesToLine: $appliesToLine, inventoryAttributeMapping: $inventoryAttributeMapping, enabled: $enabled) {
      id name category value appliesToLine inventoryAttributeMapping enabled
    }
  }
`;
const UPDATE_FACTOR = gql`
  mutation UpdateFactor($id: ID!, $name: String, $category: String, $value: Float, $appliesToLine: String, $inventoryAttributeMapping: String, $enabled: Boolean) {
    updateValuationFactor(id: $id, name: $name, category: $category, value: $value, appliesToLine: $appliesToLine, inventoryAttributeMapping: $inventoryAttributeMapping, enabled: $enabled) {
      id name category value appliesToLine inventoryAttributeMapping enabled
    }
  }
`;
const DELETE_FACTOR = gql`
  mutation DeleteFactor($id: ID!) { deleteValuationFactor(id: $id) }
`;

const CREATE_INTANGIBLE = gql`
  mutation CreateIntangible($name: String!, $appliesToLine: String, $valuationMethod: String, $defaultValue: Float!, $enabled: Boolean) {
    createIntangibleAsset(name: $name, appliesToLine: $appliesToLine, valuationMethod: $valuationMethod, defaultValue: $defaultValue, enabled: $enabled) {
      id name appliesToLine valuationMethod defaultValue enabled
    }
  }
`;
const UPDATE_INTANGIBLE = gql`
  mutation UpdateIntangible($id: ID!, $name: String, $appliesToLine: String, $valuationMethod: String, $defaultValue: Float, $enabled: Boolean) {
    updateIntangibleAsset(id: $id, name: $name, appliesToLine: $appliesToLine, valuationMethod: $valuationMethod, defaultValue: $defaultValue, enabled: $enabled) {
      id name appliesToLine valuationMethod defaultValue enabled
    }
  }
`;
const DELETE_INTANGIBLE = gql`
  mutation DeleteIntangible($id: ID!) { deleteIntangibleAsset(id: $id) }
`;

const CREATE_EXPENSE_CATEGORY = gql`
  mutation CreateExpenseCategory(
    $key: String!,
    $label: String!,
    $description: String,
    $groupName: String,
    $sortOrder: Int,
    $enabled: Boolean
  ) {
    createMultiLineExpenseCategory(
      key: $key,
      label: $label,
      description: $description,
      groupName: $groupName,
      sortOrder: $sortOrder,
      enabled: $enabled
    ) {
      id companyId key label description groupName sortOrder enabled
    }
  }
`;
const UPDATE_EXPENSE_CATEGORY = gql`
  mutation UpdateExpenseCategory(
    $id: ID!,
    $key: String,
    $label: String,
    $description: String,
    $groupName: String,
    $sortOrder: Int,
    $enabled: Boolean
  ) {
    updateMultiLineExpenseCategory(
      id: $id,
      key: $key,
      label: $label,
      description: $description,
      groupName: $groupName,
      sortOrder: $sortOrder,
      enabled: $enabled
    ) {
      id companyId key label description groupName sortOrder enabled
    }
  }
`;
const DELETE_EXPENSE_CATEGORY = gql`
  mutation DeleteExpenseCategory($id: ID!) { deleteMultiLineExpenseCategory(id: $id) }
`;

const Section = ({ title, children }) => (
  <section className="mt-4 space-y-3">
    <h3 className="text-lg font-semibold">{title}</h3>
    {children}
  </section>
);

const REVENUE_TERM_ROWS = [
  { line: 'wealth', lineLabel: 'Wealth', key: 'fa_retention_percent', defaultLabel: 'FA Retention %' },
  { line: 'wealth', lineLabel: 'Wealth', key: 'fee_based_revenue', defaultLabel: 'Fee-Based Revenue' },
  { line: 'wealth', lineLabel: 'Wealth', key: 'trailer_revenue', defaultLabel: 'Trailer Revenue' },
  { line: 'wealth', lineLabel: 'Wealth', key: 'direct_revenue', defaultLabel: 'Direct Revenue' },
  { line: 'wealth', lineLabel: 'Wealth', key: 'transactional_revenue', defaultLabel: 'Transactional Revenue' },
  { line: 'life', lineLabel: 'Life', key: 'first_year_commissions', defaultLabel: 'First Year Commissions' },
  { line: 'life', lineLabel: 'Life', key: 'renewal_commissions', defaultLabel: 'Renewal Commissions' },
  { line: 'life', lineLabel: 'Life', key: 'override_commissions', defaultLabel: 'Override Commissions' },
  { line: 'life', lineLabel: 'Life', key: 'other_revenues', defaultLabel: 'Other Revenues' },
  { line: 'group', lineLabel: 'Group', key: 'first_year_commissions', defaultLabel: 'First Year Commissions' },
  { line: 'group', lineLabel: 'Group', key: 'renewal_commissions', defaultLabel: 'Renewal Commissions' },
  { line: 'group', lineLabel: 'Group', key: 'override_commissions', defaultLabel: 'Override Commissions' },
  { line: 'group', lineLabel: 'Group', key: 'other_revenues', defaultLabel: 'Other Revenues' },
  { line: 'pc', lineLabel: 'P&C', key: 'premium', defaultLabel: 'Premium' },
  { line: 'pc', lineLabel: 'P&C', key: 'commission_rate', defaultLabel: 'Commission Rate' },
  { line: 'pc', lineLabel: 'P&C', key: 'retention_rate', defaultLabel: 'Retention Rate' },
  // Summary total-row label (rendered per tab); RIAs prefer "Total Revenue" over "Total Production".
  { line: 'wealth', lineLabel: 'Wealth', key: 'total_production', defaultLabel: 'Total Production' },
  { line: 'life', lineLabel: 'Life', key: 'total_production', defaultLabel: 'Total Production' },
  { line: 'pc', lineLabel: 'P&C', key: 'total_production', defaultLabel: 'Total Production' },
  { line: 'group', lineLabel: 'Group', key: 'total_production', defaultLabel: 'Total Production' },
  // Expenses tab — Direct Expenses label/tooltip + benchmark source disclosure.
  { line: 'expenses', lineLabel: 'Expenses', key: 'direct_expenses', defaultLabel: 'Direct Expenses' },
  { line: 'expenses', lineLabel: 'Expenses', key: 'benchmark_source', defaultLabel: '' },
];

const MultiLineAdmin = () => (
  <ApolloProvider client={client}>
    <SnackbarProvider maxSnack={3}>
    <SuccessionThemeProvider>
      <Query query={ADMIN_QUERY} fetchPolicy="network-only">
        {({ loading, error, data, refetch }) => {
          if (loading) return <div>Loading…</div>;
          if (error) return <div>Error loading admin data</div>;
          const company = data.company || {};
          const factors = data.valuationFactors || [];
          const intangibles = data.intangibleAssets || [];
          const expenseCategories = data.multiLineExpenseCategories || [];
          const industryBenchmarks = data.industryBenchmarks || [];
          // Short-circuit to a stateless child component so no hooks are created here
          return (
            <Container maxWidth="lg" style={{ paddingTop: 72 }}>
              {/* AdminBody extracted to avoid hooks inside Query render */}
              {React.createElement((props) => {
                const { enqueueSnackbar } = useSnackbar();
                const [state, setState] = useState({
                  rj_use_owners_comp_direct_expenses: company.rj_use_owners_comp_direct_expenses ?? false,
                  multi_line_mor_weight: company.multi_line_mor_weight ?? '',
                  multi_line_dcf_weight: company.multi_line_dcf_weight ?? '',
                  multi_line_ebitda_weight: company.multi_line_ebitda_weight ?? '',
                  multi_line_asset_based_weight: company.multi_line_asset_based_weight ?? '',
                  multi_line_dcf_projection_years: company.multi_line_dcf_projection_years ?? '',
                  // Confidence Weights
                  confidence_revenue_weight: company.confidence_revenue_weight ?? 1,
                  confidence_expenses_weight: company.confidence_expenses_weight ?? 1,
                  confidence_normalization_weight: company.confidence_normalization_weight ?? 1,
                  // WM
                  wm_cost_of_equity_low: company.wm_cost_of_equity_low ?? '',
                  wm_cost_of_equity_mid: company.wm_cost_of_equity_mid ?? '',
                  wm_cost_of_equity_high: company.wm_cost_of_equity_high ?? '',
                  wm_ebitda_multiple_low: company.wm_ebitda_multiple_low ?? '',
                  wm_ebitda_multiple_mid: company.wm_ebitda_multiple_mid ?? '',
                  wm_ebitda_multiple_high: company.wm_ebitda_multiple_high ?? '',
                  wm_mor_recurring_low: company.wm_mor_recurring_low ?? '',
                  wm_mor_recurring_mid: company.wm_mor_recurring_mid ?? '',
                  wm_mor_recurring_high: company.wm_mor_recurring_high ?? '',
                  wm_mor_non_recurring_low: company.wm_mor_non_recurring_low ?? '',
                  wm_mor_non_recurring_mid: company.wm_mor_non_recurring_mid ?? '',
                  wm_mor_non_recurring_high: company.wm_mor_non_recurring_high ?? '',
                  // LI
                  li_cost_of_equity_low: company.li_cost_of_equity_low ?? '',
                  li_cost_of_equity_mid: company.li_cost_of_equity_mid ?? '',
                  li_cost_of_equity_high: company.li_cost_of_equity_high ?? '',
                  li_ebitda_multiple_low: company.li_ebitda_multiple_low ?? '',
                  li_ebitda_multiple_mid: company.li_ebitda_multiple_mid ?? '',
                  li_ebitda_multiple_high: company.li_ebitda_multiple_high ?? '',
                  li_mor_first_year_low: company.li_mor_first_year_low ?? '',
                  li_mor_first_year_mid: company.li_mor_first_year_mid ?? '',
                  li_mor_first_year_high: company.li_mor_first_year_high ?? '',
                  li_mor_renewal_low: company.li_mor_renewal_low ?? '',
                  li_mor_renewal_mid: company.li_mor_renewal_mid ?? '',
                  li_mor_renewal_high: company.li_mor_renewal_high ?? '',
                  li_mor_override_low: company.li_mor_override_low ?? '',
                  li_mor_override_mid: company.li_mor_override_mid ?? '',
                  li_mor_override_high: company.li_mor_override_high ?? '',
                  // PC
                  pc_cost_of_equity_low: company.pc_cost_of_equity_low ?? '',
                  pc_cost_of_equity_mid: company.pc_cost_of_equity_mid ?? '',
                  pc_cost_of_equity_high: company.pc_cost_of_equity_high ?? '',
                  pc_ebitda_multiple_low: company.pc_ebitda_multiple_low ?? '',
                  pc_ebitda_multiple_mid: company.pc_ebitda_multiple_mid ?? '',
                  pc_ebitda_multiple_high: company.pc_ebitda_multiple_high ?? '',
                  pc_mor_personal_lines_low: company.pc_mor_personal_lines_low ?? '',
                  pc_mor_personal_lines_mid: company.pc_mor_personal_lines_mid ?? '',
                  pc_mor_personal_lines_high: company.pc_mor_personal_lines_high ?? '',
                  pc_mor_commercial_lines_low: company.pc_mor_commercial_lines_low ?? '',
                  pc_mor_commercial_lines_mid: company.pc_mor_commercial_lines_mid ?? '',
                  pc_mor_commercial_lines_high: company.pc_mor_commercial_lines_high ?? '',
                  // Group
                  group_cost_of_equity_low: company.group_cost_of_equity_low ?? '',
                  group_cost_of_equity_mid: company.group_cost_of_equity_mid ?? '',
                  group_cost_of_equity_high: company.group_cost_of_equity_high ?? '',
                  group_ebitda_multiple_low: company.group_ebitda_multiple_low ?? '',
                  group_ebitda_multiple_mid: company.group_ebitda_multiple_mid ?? '',
                  group_ebitda_multiple_high: company.group_ebitda_multiple_high ?? '',
                  group_mor_first_year_low: company.group_mor_first_year_low ?? '',
                  group_mor_first_year_mid: company.group_mor_first_year_mid ?? '',
                  group_mor_first_year_high: company.group_mor_first_year_high ?? '',
                  group_mor_renewal_low: company.group_mor_renewal_low ?? '',
                  group_mor_renewal_mid: company.group_mor_renewal_mid ?? '',
                  group_mor_renewal_high: company.group_mor_renewal_high ?? '',
                  group_mor_override_low: company.group_mor_override_low ?? '',
                  group_mor_override_mid: company.group_mor_override_mid ?? '',
                  group_mor_override_high: company.group_mor_override_high ?? '',
                });
                const [benchmarkYear, setBenchmarkYear] = useState(new Date().getFullYear());
                const [csvFile, setCsvFile] = useState(null);
                const [importLoading, setImportLoading] = useState(false);
                const [localBenchmarks, setLocalBenchmarks] = useState(industryBenchmarks);
                const [revenueTerminology, setRevenueTerminology] = useState(
                  getRevenueLineTerminology(company.revenue_line_terminology || DEFAULT_REVENUE_LINE_TERMINOLOGY)
                );
                const updateRevenueTerm = (line, key, field, value) => {
                  setRevenueTerminology((prev) => ({
                    ...prev,
                    [line]: {
                      ...(prev[line] || {}),
                      [key]: {
                        ...(prev?.[line]?.[key] || {}),
                        [field]: value,
                      },
                    },
                  }));
                };
                // Inline AdminBody to guarantee hooks run in a child, not here
                return (
                  <Mutation mutation={UPDATE_SETTINGS}>
                    {(update) => (
                      <Box py={2}>
                        <Typography variant="h6">Company Settings</Typography>
                        <Paper elevation={0} variant="outlined">
                          <Box p={2}>
                          <Grid container spacing={2}>
                            {[
                              { key: 'multi_line_mor_weight', label: 'MOR weight', step: '0.01' },
                              { key: 'multi_line_dcf_weight', label: 'DCF weight', step: '0.01' },
                              { key: 'multi_line_ebitda_weight', label: 'EBITDA weight', step: '0.01' },
                              { key: 'multi_line_asset_based_weight', label: 'Asset weight', step: '0.01' },
                              { key: 'multi_line_dcf_projection_years', label: 'DCF projection years', step: '1' },
                            ].map(({ key, label, step }) => (
                              <Grid item xs={12} sm={6} md={4} key={key}>
                                <TextField fullWidth variant="outlined" size="small" type="number" inputProps={{ step }} label={label} value={state[key]} onChange={(e)=> { const v = e.target?.value ?? ''; setState((s)=>({ ...s, [key]: v })); }} />
                              </Grid>
                            ))}
                            <Grid item xs={12}>
                              <FormControlLabel control={<Switch color="primary" checked={!!state.rj_use_owners_comp_direct_expenses} onChange={(e)=> setState((s)=> ({ ...s, rj_use_owners_comp_direct_expenses: e.target.checked }))} />} label="Use Owners Compensation / Direct Expenses" />
                            </Grid>
                          </Grid>
                      </Box>
                    </Paper>

                    <Box mt={4}>
                      <Typography variant="h6">Confidence Rating Weights</Typography>
                      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                        Configure how much each factor contributes to the overall valuation confidence score. Higher weights mean that factor has more impact. Default is 1 for balanced weighting.
                      </Typography>
                      <Paper elevation={0} variant="outlined"><Box p={2}>
                        <Grid container spacing={2}>
                          {[
                            { k:'confidence_revenue_weight', l:'Revenue Data Weight', desc: 'Weight for historical revenue / growth rate quality' },
                            { k:'confidence_expenses_weight', l:'Expenses Data Weight', desc: 'Weight for expense categorization quality' },
                            { k:'confidence_normalization_weight', l:'Normalization Weight', desc: 'Weight for normalizing adjustments' },
                          ].map(({k,l,desc})=> (
                            <Grid item xs={12} sm={4} key={k}>
                              <TextField 
                                fullWidth 
                                variant="outlined" 
                                size="small" 
                                type="number" 
                                inputProps={{ step:'0.1', min: '0', max: '10' }} 
                                label={l} 
                                helperText={desc}
                                value={state[k]} 
                                onChange={(e)=> { const v = e.target?.value ?? ''; setState((s)=>({ ...s, [k]: v })); }} 
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box></Paper>
                    </Box>

                    <Box mt={4}>
                      <Typography variant="h6">Wealth Management (WM)</Typography>
                      <Paper elevation={0} variant="outlined"><Box p={2}>
                        <Grid container spacing={2}>
                          {[
                            { k:'wm_cost_of_equity_low', l:'WM Cost of Equity (Low)' },
                            { k:'wm_cost_of_equity_mid', l:'WM Cost of Equity (Mid)' },
                            { k:'wm_cost_of_equity_high', l:'WM Cost of Equity (High)' },
                            { k:'wm_ebitda_multiple_low', l:'WM EBITDA Multiple (Low)' },
                            { k:'wm_ebitda_multiple_mid', l:'WM EBITDA Multiple (Mid)' },
                            { k:'wm_ebitda_multiple_high', l:'WM EBITDA Multiple (High)' },
                            { k:'wm_mor_recurring_low', l:'WM MOR Recurring (Low)' },
                            { k:'wm_mor_recurring_mid', l:'WM MOR Recurring (Mid)' },
                            { k:'wm_mor_recurring_high', l:'WM MOR Recurring (High)' },
                            { k:'wm_mor_non_recurring_low', l:'WM MOR Non-Recurring (Low)' },
                            { k:'wm_mor_non_recurring_mid', l:'WM MOR Non-Recurring (Mid)' },
                            { k:'wm_mor_non_recurring_high', l:'WM MOR Non-Recurring (High)' },
                          ].map(({k,l})=> (
                            <Grid item xs={12} sm={6} md={4} key={k}>
                              <TextField fullWidth variant="outlined" size="small" type="number" inputProps={{ step:'0.0001' }} label={l} value={state[k]} onChange={(e)=> { const v = e.target?.value ?? ''; setState((s)=>({ ...s, [k]: v })); }} />
                            </Grid>
                          ))}
                        </Grid>
                      </Box></Paper>
                    </Box>

                    <Box mt={4}>
                      <Typography variant="h6">Life Insurance (LI)</Typography>
                      <Paper elevation={0} variant="outlined"><Box p={2}>
                        <Grid container spacing={2}>
                          {[
                            { k:'li_cost_of_equity_low', l:'LI Cost of Equity (Low)' },
                            { k:'li_cost_of_equity_mid', l:'LI Cost of Equity (Mid)' },
                            { k:'li_cost_of_equity_high', l:'LI Cost of Equity (High)' },
                            { k:'li_ebitda_multiple_low', l:'LI EBITDA Multiple (Low)' },
                            { k:'li_ebitda_multiple_mid', l:'LI EBITDA Multiple (Mid)' },
                            { k:'li_ebitda_multiple_high', l:'LI EBITDA Multiple (High)' },
                            { k:'li_mor_first_year_low', l:'LI MOR First Year (Low)' },
                            { k:'li_mor_first_year_mid', l:'LI MOR First Year (Mid)' },
                            { k:'li_mor_first_year_high', l:'LI MOR First Year (High)' },
                            { k:'li_mor_renewal_low', l:'LI MOR Renewal (Low)' },
                            { k:'li_mor_renewal_mid', l:'LI MOR Renewal (Mid)' },
                            { k:'li_mor_renewal_high', l:'LI MOR Renewal (High)' },
                            { k:'li_mor_override_low', l:'LI MOR Override (Low)' },
                            { k:'li_mor_override_mid', l:'LI MOR Override (Mid)' },
                            { k:'li_mor_override_high', l:'LI MOR Override (High)' },
                          ].map(({k,l})=> (
                            <Grid item xs={12} sm={6} md={4} key={k}>
                              <TextField fullWidth variant="outlined" size="small" type="number" inputProps={{ step:'0.0001' }} label={l} value={state[k]} onChange={(e)=> { const v = e.target?.value ?? ''; setState((s)=>({ ...s, [k]: v })); }} />
                            </Grid>
                          ))}
                        </Grid>
                      </Box></Paper>
                    </Box>

                    <Box mt={4}>
                      <Typography variant="h6">Property & Casualty (PC)</Typography>
                      <Paper elevation={0} variant="outlined"><Box p={2}>
                        <Grid container spacing={2}>
                          {[
                            { k:'pc_cost_of_equity_low', l:'PC Cost of Equity (Low)' },
                            { k:'pc_cost_of_equity_mid', l:'PC Cost of Equity (Mid)' },
                            { k:'pc_cost_of_equity_high', l:'PC Cost of Equity (High)' },
                            { k:'pc_ebitda_multiple_low', l:'PC EBITDA Multiple (Low)' },
                            { k:'pc_ebitda_multiple_mid', l:'PC EBITDA Multiple (Mid)' },
                            { k:'pc_ebitda_multiple_high', l:'PC EBITDA Multiple (High)' },
                            { k:'pc_mor_personal_lines_low', l:'PC MOR Personal Lines (Low)' },
                            { k:'pc_mor_personal_lines_mid', l:'PC MOR Personal Lines (Mid)' },
                            { k:'pc_mor_personal_lines_high', l:'PC MOR Personal Lines (High)' },
                            { k:'pc_mor_commercial_lines_low', l:'PC MOR Commercial Lines (Low)' },
                            { k:'pc_mor_commercial_lines_mid', l:'PC MOR Commercial Lines (Mid)' },
                            { k:'pc_mor_commercial_lines_high', l:'PC MOR Commercial Lines (High)' },
                          ].map(({k,l})=> (
                            <Grid item xs={12} sm={6} md={4} key={k}>
                              <TextField fullWidth variant="outlined" size="small" type="number" inputProps={{ step:'0.0001' }} label={l} value={state[k]} onChange={(e)=> { const v = e.target?.value ?? ''; setState((s)=>({ ...s, [k]: v })); }} />
                            </Grid>
                          ))}
                        </Grid>
                      </Box></Paper>
                    </Box>

                    <Box mt={4}>
                      <Typography variant="h6">Group Insurance</Typography>
                      <Paper elevation={0} variant="outlined"><Box p={2}>
                        <Grid container spacing={2}>
                          {[
                            { k:'group_cost_of_equity_low', l:'Group Cost of Equity (Low)' },
                            { k:'group_cost_of_equity_mid', l:'Group Cost of Equity (Mid)' },
                            { k:'group_cost_of_equity_high', l:'Group Cost of Equity (High)' },
                            { k:'group_ebitda_multiple_low', l:'Group EBITDA Multiple (Low)' },
                            { k:'group_ebitda_multiple_mid', l:'Group EBITDA Multiple (Mid)' },
                            { k:'group_ebitda_multiple_high', l:'Group EBITDA Multiple (High)' },
                            { k:'group_mor_first_year_low', l:'Group MOR First Year (Low)' },
                            { k:'group_mor_first_year_mid', l:'Group MOR First Year (Mid)' },
                            { k:'group_mor_first_year_high', l:'Group MOR First Year (High)' },
                            { k:'group_mor_renewal_low', l:'Group MOR Renewal (Low)' },
                            { k:'group_mor_renewal_mid', l:'Group MOR Renewal (Mid)' },
                            { k:'group_mor_renewal_high', l:'Group MOR Renewal (High)' },
                            { k:'group_mor_override_low', l:'Group MOR Override (Low)' },
                            { k:'group_mor_override_mid', l:'Group MOR Override (Mid)' },
                            { k:'group_mor_override_high', l:'Group MOR Override (High)' },
                          ].map(({k,l})=> (
                            <Grid item xs={12} sm={6} md={4} key={k}>
                              <TextField fullWidth variant="outlined" size="small" type="number" inputProps={{ step:'0.0001' }} label={l} value={state[k]} onChange={(e)=> { const v = e.target?.value ?? ''; setState((s)=>({ ...s, [k]: v })); }} />
                            </Grid>
                          ))}
                        </Grid>
                      </Box></Paper>
                    </Box>

                    <Box mt={4}>
                      <Typography variant="h6">Revenue Line Terminology</Typography>
                      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                        Customize display names and tooltip definitions for revenue inputs shown in valuation entry forms.
                      </Typography>
                      <Paper elevation={0} variant="outlined">
                        <Box p={2}>
                          <Grid container spacing={2}>
                            {REVENUE_TERM_ROWS.map((row) => (
                              <React.Fragment key={`${row.line}_${row.key}`}>
                                <Grid item xs={12} sm={2}>
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Line"
                                    value={row.lineLabel}
                                    InputProps={{ readOnly: true }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Display Name"
                                    value={revenueTerminology?.[row.line]?.[row.key]?.label || row.defaultLabel}
                                    onChange={(e) => updateRevenueTerm(row.line, row.key, 'label', e.target.value)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Tooltip Definition"
                                    value={revenueTerminology?.[row.line]?.[row.key]?.definition || ''}
                                    onChange={(e) => updateRevenueTerm(row.line, row.key, 'definition', e.target.value)}
                                  />
                                </Grid>
                              </React.Fragment>
                            ))}
                          </Grid>
                        </Box>
                      </Paper>
                    </Box>

                    <Box mt={4} mb={1}><Typography variant="h6">Valuation Factors</Typography></Box>
                      <Mutation mutation={CREATE_FACTOR}>
                        {(create) => (
                          <Button variant="outlined" onClick={async()=>{ await create({ variables: { name: 'New Factor', category: 'synergy', value: 0.01 } }); refetch(); }}>Add Factor</Button>
                        )}
                      </Mutation>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {['Name','Category','Value','Line','Inventory Mapping','Enabled','Actions'].map((h)=>(<TableCell key={h}>{h}</TableCell>))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {factors.map((f)=> (
                              <Mutation key={`uf-${f.id}`} mutation={UPDATE_FACTOR}>
                                {(update)=> (
                                  <TableRow>
                                    <TableCell><TextField defaultValue={f.name} onBlur={(e)=>update({ variables: { id: f.id, name: e.target.value } }).then(()=>refetch())} size="small" /></TableCell>
                                    <TableCell>
                                      <FormControl fullWidth size="small" variant="outlined">
                                        <Select defaultValue={f.category} onChange={(e)=>update({ variables: { id: f.id, category: e.target.value } }).then(()=>refetch())}>
                                          {['synergy','enhancement','risk'].map(opt=> <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                        </Select>
                                      </FormControl>
                                    </TableCell>
                                    <TableCell><TextField type="number" inputProps={{ step: '0.0001' }} defaultValue={f.value} onBlur={(e)=>update({ variables: { id: f.id, value: parseFloat(e.target.value) } }).then(()=>refetch())} size="small" /></TableCell>
                                    <TableCell>
                                      <FormControl fullWidth size="small" variant="outlined">
                                        <Select defaultValue={f.appliesToLine || ''} onChange={(e)=>update({ variables: { id: f.id, appliesToLine: e.target.value || null } }).then(()=>refetch())}>
                                          <MenuItem value="">All</MenuItem>
                                          <MenuItem value="wealth">Wealth</MenuItem>
                                          <MenuItem value="life">Life</MenuItem>
                                          <MenuItem value="pc">P&C</MenuItem>
                                          <MenuItem value="group">Group</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </TableCell>
                                    <TableCell><TextField defaultValue={f.inventoryAttributeMapping || ''} onBlur={(e)=>update({ variables: { id: f.id, inventoryAttributeMapping: e.target.value || null } }).then(()=>refetch())} size="small" /></TableCell>
                                    <TableCell><FormControlLabel control={<Switch color="primary" defaultChecked={!!f.enabled} onChange={(e)=>update({ variables: { id: f.id, enabled: e.target.checked } }).then(()=>refetch())} />} label="" /></TableCell>
                                    <TableCell>
                                      <Mutation mutation={DELETE_FACTOR}>{(del)=>(
                                        <Button color="secondary" onClick={async()=>{ await del({ variables: { id: f.id } }); refetch(); }}>Delete</Button>
                                      )}</Mutation>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </Mutation>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                    <Box mt={4} mb={1}><Typography variant="h6">Intangible Assets</Typography></Box>
                      <Mutation mutation={CREATE_INTANGIBLE}>
                        {(create) => (
                          <Button variant="outlined" onClick={async()=>{ await create({ variables: { name: 'New Intangible', defaultValue: 0.0 } }); refetch(); }}>Add Intangible</Button>
                        )}
                      </Mutation>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {['Name','Line','Method','Default Value','Enabled','Actions'].map((h)=>(<TableCell key={h}>{h}</TableCell>))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {intangibles.map((it)=> (
                              <Mutation key={`ui-${it.id}`} mutation={UPDATE_INTANGIBLE}>
                                {(update)=> (
                                  <TableRow>
                                    <TableCell><TextField defaultValue={it.name} size="small" onBlur={(e)=>update({ variables: { id: it.id, name: e.target.value } }).then(()=>refetch())} /></TableCell>
                                    <TableCell>
                                      <FormControl fullWidth size="small" variant="outlined">
                                        <Select defaultValue={it.appliesToLine || ''} onChange={(e)=>update({ variables: { id: it.id, appliesToLine: e.target.value || null } }).then(()=>refetch())}>
                                          <MenuItem value="">All</MenuItem>
                                          <MenuItem value="wealth">Wealth</MenuItem>
                                          <MenuItem value="life">Life</MenuItem>
                                          <MenuItem value="pc">P&C</MenuItem>
                                          <MenuItem value="group">Group</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </TableCell>
                                    <TableCell><TextField defaultValue={it.valuationMethod || ''} size="small" onBlur={(e)=>update({ variables: { id: it.id, valuationMethod: e.target.value || null } }).then(()=>refetch())} /></TableCell>
                                    <TableCell><TextField type="number" inputProps={{ step: '0.01' }} defaultValue={it.defaultValue} size="small" onBlur={(e)=>update({ variables: { id: it.id, defaultValue: parseFloat(e.target.value) } }).then(()=>refetch())} /></TableCell>
                                    <TableCell><FormControlLabel control={<Switch color="primary" defaultChecked={!!it.enabled} onChange={(e)=>update({ variables: { id: it.id, enabled: e.target.checked } }).then(()=>refetch())} />} label="" /></TableCell>
                                    <TableCell>
                                      <Mutation mutation={DELETE_INTANGIBLE}>{(del)=>(
                                        <Button color="secondary" onClick={async()=>{ await del({ variables: { id: it.id } }); refetch(); }}>Delete</Button>
                                      )}</Mutation>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </Mutation>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                    <Box mt={4} mb={1}><Typography variant="h6">Operating Expense Categories</Typography></Box>
                      <Mutation mutation={CREATE_EXPENSE_CATEGORY}>
                        {(create) => (
                          <Button
                            variant="outlined"
                            onClick={async()=>{
                              await create({
                                variables: {
                                  key: `category_${Date.now()}`,
                                  label: 'New Category',
                                  description: '',
                                  groupName: 'Other Expenses',
                                  sortOrder: expenseCategories.length,
                                  enabled: true,
                                }
                              });
                              refetch();
                            }}
                          >
                            Add Category
                          </Button>
                        )}
                      </Mutation>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {['Label','Description','Group','Sort Order','Enabled','Actions'].map((h)=>(<TableCell key={h}>{h}</TableCell>))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {expenseCategories.map((c)=> (
                              <Mutation key={`uec-${c.id}`} mutation={UPDATE_EXPENSE_CATEGORY}>
                                {(update)=> (
                                  <TableRow>
                                    <TableCell>
                                      <TextField
                                        defaultValue={c.label}
                                        size="small"
                                        onBlur={(e)=>update({ variables: { id: c.id, label: e.target.value } }).then(()=>refetch())}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        defaultValue={c.description || ''}
                                        size="small"
                                        fullWidth
                                        onBlur={(e)=>update({ variables: { id: c.id, description: e.target.value } }).then(()=>refetch())}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        defaultValue={c.groupName || ''}
                                        size="small"
                                        onBlur={(e)=>update({ variables: { id: c.id, groupName: e.target.value } }).then(()=>refetch())}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        type="number"
                                        inputProps={{ step: '1' }}
                                        defaultValue={c.sortOrder ?? 0}
                                        size="small"
                                        onBlur={(e)=> {
                                          const v = e.target.value;
                                          update({ variables: { id: c.id, sortOrder: v === '' ? 0 : parseInt(v, 10) } }).then(()=>refetch());
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            color="primary"
                                            defaultChecked={!!c.enabled}
                                            onChange={(e)=>update({ variables: { id: c.id, enabled: e.target.checked } }).then(()=>refetch())}
                                          />
                                        }
                                        label=""
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Mutation mutation={DELETE_EXPENSE_CATEGORY}>
                                        {(del)=>(
                                          <Button
                                            color="secondary"
                                            onClick={async()=>{ await del({ variables: { id: c.id } }); refetch(); }}
                                          >
                                            Delete
                                          </Button>
                                        )}
                                      </Mutation>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </Mutation>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Box mt={4} mb={1}><Typography variant="h6">Industry Benchmarks</Typography></Box>
                      <Paper elevation={0} variant="outlined">
                        <Box p={2}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={4}>
                              <TextField
                                type="file"
                                inputProps={{ accept: '.csv' }}
                                fullWidth
                                variant="outlined"
                                size="small"
                                onChange={(e) => setCsvFile(e.target.files[0])}
                                InputLabelProps={{ shrink: true }}
                                label="CSV File"
                              />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <TextField
                                type="number"
                                label="Year"
                                value={benchmarkYear}
                                onChange={(e) => setBenchmarkYear(parseInt(e.target.value, 10) || new Date().getFullYear())}
                                fullWidth
                                variant="outlined"
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Mutation mutation={IMPORT_BENCHMARKS}>
                                {(importBenchmarks) => (
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={!csvFile || importLoading}
                                    onClick={async () => {
                                      if (!csvFile) return;
                                      setImportLoading(true);
                                      try {
                                        const csvContent = await csvFile.text();
                                        const result = await importBenchmarks({
                                          variables: { csvContent, year: benchmarkYear }
                                        });
                                        const data = result.data.importIndustryBenchmarks;
                                        if (data.success) {
                                          enqueueSnackbar(`Import successful: ${data.createdCount} created, ${data.updatedCount} updated`, { variant: 'success' });
                                          setLocalBenchmarks(data.benchmarks || []);
                                          refetch();
                                        } else {
                                          enqueueSnackbar(`Import failed: ${data.errors.join(', ')}`, { variant: 'error' });
                                        }
                                      } catch (err) {
                                        enqueueSnackbar(`Import error: ${err.message}`, { variant: 'error' });
                                      } finally {
                                        setImportLoading(false);
                                        setCsvFile(null);
                                      }
                                    }}
                                  >
                                    {importLoading ? 'Importing...' : 'Import CSV'}
                                  </Button>
                                )}
                              </Mutation>
                            </Grid>
                          </Grid>

                          {localBenchmarks.length > 0 && (
                            <Box mt={2}>
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Tier</TableCell>
                                      <TableCell>Year</TableCell>
                                      <TableCell align="right">Min Revenue</TableCell>
                                      <TableCell align="right">Max Revenue</TableCell>
                                      <TableCell align="right">Owner Salary %</TableCell>
                                      <TableCell align="right">Operating Exp %</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {localBenchmarks.map((b) => (
                                      <TableRow key={b.id}>
                                        <TableCell>{b.tierName}</TableCell>
                                        <TableCell>{b.year}</TableCell>
                                        <TableCell align="right">
                                          {b.minRevenue != null ? `$${Number(b.minRevenue).toLocaleString()}` : '-'}
                                        </TableCell>
                                        <TableCell align="right">
                                          {b.maxRevenue != null ? `$${Number(b.maxRevenue).toLocaleString()}` : 'Unlimited'}
                                        </TableCell>
                                        <TableCell align="right">
                                          {b.ownerSalaryDrawPercent != null ? `${(b.ownerSalaryDrawPercent * 100).toFixed(1)}%` : '-'}
                                        </TableCell>
                                        <TableCell align="right">
                                          {b.totalOperatingExpensesPercent != null ? `${(b.totalOperatingExpensesPercent * 100).toFixed(1)}%` : '-'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}
                        </Box>
                      </Paper>

                      <Box mt={4} display="flex" justifyContent="flex-end">
                        <Button
                          color="primary"
                          variant="contained"
                          size="large"
                          onClick={async () => {
                            const floatVal = (k) => {
                              const v = state[k] !== '' && state[k] != null ? state[k] : company[k];
                              const n = parseFloat(v);
                              return Number.isFinite(n) ? n : undefined;
                            };
                            await update({
                              variables: {
                                multiLineMorWeight: floatVal('multi_line_mor_weight'),
                                multiLineDcfWeight: floatVal('multi_line_dcf_weight'),
                                multiLineEbitdaWeight: floatVal('multi_line_ebitda_weight'),
                                multiLineAssetBasedWeight: floatVal('multi_line_asset_based_weight'),
                                multiLineDcfProjectionYears: parseInt(state.multi_line_dcf_projection_years || company.multi_line_dcf_projection_years, 10),
                                rjUseOwnersCompDirectExpenses: !!state.rj_use_owners_comp_direct_expenses,
                                // Confidence Weights
                                confidenceRevenueWeight: floatVal('confidence_revenue_weight'),
                                confidenceExpensesWeight: floatVal('confidence_expenses_weight'),
                                confidenceNormalizationWeight: floatVal('confidence_normalization_weight'),
                                // WM
                                wmCostOfEquityLow: floatVal('wm_cost_of_equity_low'),
                                wmCostOfEquityMid: floatVal('wm_cost_of_equity_mid'),
                                wmCostOfEquityHigh: floatVal('wm_cost_of_equity_high'),
                                wmEbitdaMultipleLow: floatVal('wm_ebitda_multiple_low'),
                                wmEbitdaMultipleMid: floatVal('wm_ebitda_multiple_mid'),
                                wmEbitdaMultipleHigh: floatVal('wm_ebitda_multiple_high'),
                                wmMorRecurringLow: floatVal('wm_mor_recurring_low'),
                                wmMorRecurringMid: floatVal('wm_mor_recurring_mid'),
                                wmMorRecurringHigh: floatVal('wm_mor_recurring_high'),
                                wmMorNonRecurringLow: floatVal('wm_mor_non_recurring_low'),
                                wmMorNonRecurringMid: floatVal('wm_mor_non_recurring_mid'),
                                wmMorNonRecurringHigh: floatVal('wm_mor_non_recurring_high'),
                                // LI
                                liCostOfEquityLow: floatVal('li_cost_of_equity_low'),
                                liCostOfEquityMid: floatVal('li_cost_of_equity_mid'),
                                liCostOfEquityHigh: floatVal('li_cost_of_equity_high'),
                                liEbitdaMultipleLow: floatVal('li_ebitda_multiple_low'),
                                liEbitdaMultipleMid: floatVal('li_ebitda_multiple_mid'),
                                liEbitdaMultipleHigh: floatVal('li_ebitda_multiple_high'),
                                liMorFirstYearLow: floatVal('li_mor_first_year_low'),
                                liMorFirstYearMid: floatVal('li_mor_first_year_mid'),
                                liMorFirstYearHigh: floatVal('li_mor_first_year_high'),
                                liMorRenewalLow: floatVal('li_mor_renewal_low'),
                                liMorRenewalMid: floatVal('li_mor_renewal_mid'),
                                liMorRenewalHigh: floatVal('li_mor_renewal_high'),
                                liMorOverrideLow: floatVal('li_mor_override_low'),
                                liMorOverrideMid: floatVal('li_mor_override_mid'),
                                liMorOverrideHigh: floatVal('li_mor_override_high'),
                                // PC
                                pcCostOfEquityLow: floatVal('pc_cost_of_equity_low'),
                                pcCostOfEquityMid: floatVal('pc_cost_of_equity_mid'),
                                pcCostOfEquityHigh: floatVal('pc_cost_of_equity_high'),
                                pcEbitdaMultipleLow: floatVal('pc_ebitda_multiple_low'),
                                pcEbitdaMultipleMid: floatVal('pc_ebitda_multiple_mid'),
                                pcEbitdaMultipleHigh: floatVal('pc_ebitda_multiple_high'),
                                pcMorPersonalLinesLow: floatVal('pc_mor_personal_lines_low'),
                                pcMorPersonalLinesMid: floatVal('pc_mor_personal_lines_mid'),
                                pcMorPersonalLinesHigh: floatVal('pc_mor_personal_lines_high'),
                                pcMorCommercialLinesLow: floatVal('pc_mor_commercial_lines_low'),
                                pcMorCommercialLinesMid: floatVal('pc_mor_commercial_lines_mid'),
                                pcMorCommercialLinesHigh: floatVal('pc_mor_commercial_lines_high'),
                                // Group
                                groupCostOfEquityLow: floatVal('group_cost_of_equity_low'),
                                groupCostOfEquityMid: floatVal('group_cost_of_equity_mid'),
                                groupCostOfEquityHigh: floatVal('group_cost_of_equity_high'),
                                groupEbitdaMultipleLow: floatVal('group_ebitda_multiple_low'),
                                groupEbitdaMultipleMid: floatVal('group_ebitda_multiple_mid'),
                                groupEbitdaMultipleHigh: floatVal('group_ebitda_multiple_high'),
                                groupMorFirstYearLow: floatVal('group_mor_first_year_low'),
                                groupMorFirstYearMid: floatVal('group_mor_first_year_mid'),
                                groupMorFirstYearHigh: floatVal('group_mor_first_year_high'),
                                groupMorRenewalLow: floatVal('group_mor_renewal_low'),
                                groupMorRenewalMid: floatVal('group_mor_renewal_mid'),
                                groupMorRenewalHigh: floatVal('group_mor_renewal_high'),
                                groupMorOverrideLow: floatVal('group_mor_override_low'),
                                groupMorOverrideMid: floatVal('group_mor_override_mid'),
                                groupMorOverrideHigh: floatVal('group_mor_override_high'),
                                revenueLineTerminology: revenueTerminology,
                              },
                            });
                            refetch();
                            enqueueSnackbar('Settings saved successfully', { variant: 'success' });
                          }}
                        >
                          Save Settings
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Mutation>
              );
              })}
            </Container>
          );
          const [weightState, setWeightState] = useState({
            multi_line_mor_weight: '',
            multi_line_dcf_weight: '',
            multi_line_ebitda_weight: '',
            multi_line_asset_based_weight: '',
            multi_line_dcf_projection_years: '',
          });
          return (
            <div className="p-4 space-y-6">
              <Section title="Company Settings">
                <Mutation mutation={UPDATE_SETTINGS}>
                  {(update, { loading: saving }) => (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-3xl">
                      {[
                        { key: 'multi_line_mor_weight', label: 'MOR weight', step: '0.01' },
                        { key: 'multi_line_dcf_weight', label: 'DCF weight', step: '0.01' },
                        { key: 'multi_line_ebitda_weight', label: 'EBITDA weight', step: '0.01' },
                        { key: 'multi_line_asset_based_weight', label: 'Asset weight', step: '0.01' },
                        { key: 'multi_line_dcf_projection_years', label: 'DCF projection years', step: '1' },
                      ].map(({ key, label, step }) => (
                        <label key={key} className="text-sm block">
                          <span className="text-succession-text-secondary">{label}</span>
                          <input
                            type="number"
                            step={step}
                            className="mt-1 w-full rounded border border-succession-border px-2 py-1"
                            value={weightState[key] !== '' && weightState[key] != null ? weightState[key] : (company[key] ?? '')}
                            onChange={(e)=> { const v = e.target?.value ?? ''; setWeightState((s)=>({ ...s, [key]: v })); }}
                          />
                        </label>
                      ))}
                      <label className="text-sm flex items-center gap-2 col-span-full">
                        <input type="checkbox" defaultChecked={!!company.rj_use_owners_comp_direct_expenses} onChange={(e)=> setWeightState((s)=> ({ ...s, rj_use_owners_comp_direct_expenses: e.target.checked }))} />
                        Use Owners Compensation / Direct Expenses
                      </label>
                      <button
                        className="col-span-full px-3 py-2 rounded bg-succession-primary text-white"
                        onClick={async()=>{
                          const nFloat = (k) => {
                            const v = weightState[k] !== '' && weightState[k] != null ? weightState[k] : company[k];
                            const n = parseFloat(v);
                            return Number.isFinite(n) ? n : undefined;
                          };
                          const nInt = (k) => {
                            const v = weightState[k] !== '' && weightState[k] != null ? weightState[k] : company[k];
                            const n = parseInt(v, 10);
                            return Number.isFinite(n) ? n : undefined;
                          };
                          await update({ variables: {
                            multiLineMorWeight: nFloat('multi_line_mor_weight'),
                            multiLineDcfWeight: nFloat('multi_line_dcf_weight'),
                            multiLineEbitdaWeight: nFloat('multi_line_ebitda_weight'),
                            multiLineAssetBasedWeight: nFloat('multi_line_asset_based_weight'),
                            multiLineDcfProjectionYears: nInt('multi_line_dcf_projection_years'),
                            rjUseOwnersCompDirectExpenses: (weightState.rj_use_owners_comp_direct_expenses != null ? !!weightState.rj_use_owners_comp_direct_expenses : !!company.rj_use_owners_comp_direct_expenses),
                          }});
                          refetch();
                        }}
                      >
                        Save Settings
                      </button>
                    </div>
                  )}
                </Mutation>
              </Section>

              <Section title="Valuation Factors">
                <Mutation mutation={CREATE_FACTOR}>
                  {(create) => (
                    <button className="px-3 py-2 rounded border border-succession-border" onClick={async()=>{ await create({ variables: { name: 'New Factor', category: 'synergy', value: 0.01 } }); refetch(); }}>Add Factor</button>
                  )}
                </Mutation>
                <div className="overflow-x-auto rounded-md border border-succession-border">
                  <table className="min-w-full table-auto text-sm">
                    <thead className="bg-succession-bg-tertiary"><tr>
                      {['Name','Category','Value','Line','Inventory Mapping','Enabled','Actions'].map((h)=>(<th key={h} className="px-3 py-2 text-left text-succession-text-secondary font-medium">{h}</th>))}
                    </tr></thead>
                    <tbody className="divide-y divide-succession-border">
                      {factors.map((f)=> (
                        <Mutation key={`uf-${f.id}`} mutation={UPDATE_FACTOR}>
                          {(update)=> (
                            <tr>
                              <td className="px-3 py-2"><input defaultValue={f.name} className="w-full" onBlur={(e)=>update({ variables: { id: f.id, name: e.target.value } }).then(()=>refetch())} /></td>
                              <td className="px-3 py-2">
                                <select defaultValue={f.category} className="w-full" onChange={(e)=>update({ variables: { id: f.id, category: e.target.value } }).then(()=>refetch())}>
                                  {['synergy','enhancement','risk'].map(opt=> <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2"><input type="number" step="0.0001" defaultValue={f.value} className="w-full" onBlur={(e)=>update({ variables: { id: f.id, value: parseFloat(e.target.value) } }).then(()=>refetch())} /></td>
                              <td className="px-3 py-2">
                                <select defaultValue={f.appliesToLine || ''} className="w-full" onChange={(e)=>update({ variables: { id: f.id, appliesToLine: e.target.value || null } }).then(()=>refetch())}>
                                  <option value="">All</option>
                                  <option value="wealth">Wealth</option>
                                  <option value="life">Life</option>
                                  <option value="pc">P&C</option>
                                  <option value="group">Group</option>
                                </select>
                              </td>
                              <td className="px-3 py-2"><input defaultValue={f.inventoryAttributeMapping || ''} className="w-full" onBlur={(e)=>update({ variables: { id: f.id, inventoryAttributeMapping: e.target.value || null } }).then(()=>refetch())} /></td>
                              <td className="px-3 py-2"><input type="checkbox" defaultChecked={!!f.enabled} onChange={(e)=>update({ variables: { id: f.id, enabled: e.target.checked } }).then(()=>refetch())} /></td>
                              <td className="px-3 py-2">
                                <Mutation mutation={DELETE_FACTOR}>{(del)=>(
                                  <button className="text-red-600" onClick={async()=>{ await del({ variables: { id: f.id } }); refetch(); }}>Delete</button>
                                )}</Mutation>
                              </td>
                            </tr>
                          )}
                        </Mutation>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>

              <Section title="Intangible Assets">
                <Mutation mutation={CREATE_INTANGIBLE}>
                  {(create) => (
                    <button className="px-3 py-2 rounded border border-succession-border" onClick={async()=>{ await create({ variables: { name: 'New Intangible', defaultValue: 0.0 } }); refetch(); }}>Add Intangible</button>
                  )}
                </Mutation>
                <div className="overflow-x-auto rounded-md border border-succession-border">
                  <table className="min-w-full table-auto text-sm">
                    <thead className="bg-succession-bg-tertiary"><tr>
                      {['Name','Line','Method','Default Value','Enabled','Actions'].map((h)=>(<th key={h} className="px-3 py-2 text-left text-succession-text-secondary font-medium">{h}</th>))}
                    </tr></thead>
                    <tbody className="divide-y divide-succession-border">
                      {intangibles.map((it)=> (
                        <Mutation key={`ui-${it.id}`} mutation={UPDATE_INTANGIBLE}>
                          {(update)=> (
                            <tr>
                              <td className="px-3 py-2"><input defaultValue={it.name} className="w-full" onBlur={(e)=>update({ variables: { id: it.id, name: e.target.value } }).then(()=>refetch())} /></td>
                              <td className="px-3 py-2">
                                <select defaultValue={it.appliesToLine || ''} className="w-full" onChange={(e)=>update({ variables: { id: it.id, appliesToLine: e.target.value || null } }).then(()=>refetch())}>
                                  <option value="">All</option>
                                  <option value="wealth">Wealth</option>
                                  <option value="life">Life</option>
                                  <option value="pc">P&C</option>
                                  <option value="group">Group</option>
                                </select>
                              </td>
                              <td className="px-3 py-2"><input defaultValue={it.valuationMethod || ''} className="w-full" onBlur={(e)=>update({ variables: { id: it.id, valuationMethod: e.target.value || null } }).then(()=>refetch())} /></td>
                              <td className="px-3 py-2"><input type="number" step="0.01" defaultValue={it.defaultValue} className="w-full" onBlur={(e)=>update({ variables: { id: it.id, defaultValue: parseFloat(e.target.value) } }).then(()=>refetch())} /></td>
                              <td className="px-3 py-2"><input type="checkbox" defaultChecked={!!it.enabled} onChange={(e)=>update({ variables: { id: it.id, enabled: e.target.checked } }).then(()=>refetch())} /></td>
                              <td className="px-3 py-2">
                                <Mutation mutation={DELETE_INTANGIBLE}>{(del)=>(
                                  <button className="text-red-600" onClick={async()=>{ await del({ variables: { id: it.id } }); refetch(); }}>Delete</button>
                                )}</Mutation>
                              </td>
                            </tr>
                          )}
                        </Mutation>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            </div>
          );
        }}
      </Query>
    </SuccessionThemeProvider>
    </SnackbarProvider>
  </ApolloProvider>
);

export default MultiLineAdmin;


